<!-- src/views/admin/AdminOnboardingManageFormTextView.vue -->
<template>
  <div class="min-h-screen bg-[#F0F0F0] font-sans">
    
    <!-- 1. PREMIUM HEADER CARD -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 no-print">
      <div class="bg-[#0A1630] rounded-[40px] p-8 border-b-4 border-[#20FCF1] shadow-2xl relative overflow-hidden">
        <div class="absolute top-0 right-0 p-12 opacity-5">
          <IdentificationIcon class="h-32 w-32 text-white" />
        </div>
        
        <div class="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h1 class="text-3xl font-black text-white uppercase tracking-tight leading-none">Onboarding Registry</h1>
            <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.2em] mt-3">Personnel Declarations & Form Content</p>
          </div>
          
          <router-link to="/admin/onboarding-records" class="bg-white/5 border border-white/10 text-white px-6 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-white/10 transition-all flex items-center gap-2">
            <ArrowUturnLeftIcon class="h-4 w-4 text-[#20FCF1]" /> <span>Return to Records</span>
          </router-link>
        </div>
      </div>
    </div>

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      
      <!-- LOADING / ERROR STATES -->
      <div v-if="loading" class="flex flex-col items-center justify-center py-20">
        <svg class="animate-spin h-10 w-10 text-[#20FCF1]" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
        <p class="micro-label mt-4">Hydrating Onboarding Registry Content...</p>
      </div>

      <div v-else-if="error" class="bg-rose-50 border border-rose-100 p-10 rounded-[40px] text-center">
        <p class="text-rose-600 font-bold mb-4 uppercase tracking-tight">{{ error }}</p>
        <button @click="window.location.reload()" class="bg-[#0A1630] text-white px-8 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest">Retry Connection</button>
      </div>
      
      <div v-else class="space-y-12">
        <!-- Loop through each FORM SECTION -->
        <div v-for="section in formTextSections" :key="section.id" class="bg-white rounded-[40px] border border-slate-100 shadow-xl overflow-hidden animate-fadeIn">
          
          <div class="bg-slate-50 border-b border-slate-100 p-8 flex justify-between items-center">
            <h2 class="text-xl font-black text-[#0A1630] uppercase tracking-tight">{{ section.title }}</h2>
            <div v-if="!editMode[section.id]" class="flex items-center gap-2">
               <div class="w-2 h-2 rounded-full bg-[#20FCF1] animate-pulse"></div>
               <span class="text-[10px] font-black text-slate-400 uppercase tracking-widest">Live Registry Entry</span>
            </div>
          </div>
          
          <div class="p-8 md:p-12">
            <label class="micro-label mb-4 text-[#20FCF1]">Introductory & Declaration Protocol</label>
            
            <!-- READ MODE -->
            <div v-if="!editMode[section.id]" class="group relative">
              <div class="text-display-box p-8 bg-slate-50 rounded-[32px] border border-slate-100 shadow-inner">
                <div 
                  class="ql-editor !p-0 custom-prose-styles" 
                  v-html="textDataMap[section.id] || '<p><em>No protocol content defined. System defaults are currently active. Click edit to initiate custom text.</em></p>'"
                ></div>
              </div>
              <button @click="startEditing(section.id)" class="absolute top-4 right-4 bg-white border border-slate-200 p-3 rounded-xl shadow-sm hover:border-[#20FCF1] hover:text-[#FD5E00] transition-all">
                <PencilSquareIcon class="h-5 w-5" />
              </button>
            </div>
            
            <!-- EDIT MODE (Rich Text Editor) -->
            <div v-if="editMode[section.id]" class="space-y-6">
              <div class="quill-wrapper border border-slate-200 rounded-[32px] overflow-hidden shadow-inner bg-white">
                <QuillEditor 
                  v-model:content="editData[section.id]" 
                  contentType="html"
                  theme="snow"
                  :toolbar="toolbarOptions"
                  class="min-h-[400px]"
                />
              </div>
              
              <div class="flex items-center justify-between bg-slate-900 p-6 rounded-[24px]">
                <div class="flex items-center gap-3">
                  <InformationCircleIcon class="h-5 w-5 text-[#20FCF1]" />
                  <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Ensure formatting adheres to client branding standards.</p>
                </div>
                <div class="flex gap-4">
                  <button @click="cancelEditing(section.id)" class="px-6 py-3 text-[10px] font-black uppercase text-slate-400 hover:text-white transition-colors">Discard</button>
                  <button 
                    @click="saveText(section.id)" 
                    :disabled="isSaving" 
                    class="bg-[#20FCF1] text-[#0A1630] px-10 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest hover:scale-[1.05] active:scale-95 transition-all shadow-lg disabled:opacity-50 flex items-center gap-2"
                  >
                    <span v-if="isSaving" class="animate-spin h-3 w-3 border-2 border-[#0A1630] border-t-transparent rounded-full"></span>
                    <span>{{ isSaving ? 'Authorising Update...' : 'Update Registry' }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- REGISTRY STANDARDS FOOTER -->
        <div class="bg-[#0A1630] p-10 rounded-[40px] text-white shadow-2xl relative overflow-hidden">
          <div class="absolute -right-10 -bottom-10 opacity-5"><ShieldCheckIcon class="w-64 h-64 text-[#20FCF1]" /></div>
          <h3 class="text-sm font-black text-[#20FCF1] uppercase tracking-widest mb-4">Onboarding Integrity</h3>
          <p class="text-xs text-slate-400 leading-relaxed max-w-2xl">
            Protocol text updates are applied instantly to new onboarding sessions. For sections involving contracts or health assessments, please ensure the wording is strictly authorised by the relevant HR or Clinical department.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { db, auth } from '@/firebaseConfig';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';
import { 
  IdentificationIcon, 
  ArrowUturnLeftIcon, 
  PencilSquareIcon, 
  InformationCircleIcon,
  ShieldCheckIcon
} from '@heroicons/vue/24/outline';

const loading = ref(true);
const error = ref(null);
const textDataMap = ref({});
const editMode = ref({});
const editData = ref({});
const isSaving = ref(false);
const currentClientId = ref(null);

const toolbarOptions = [
  ['bold', 'italic', 'underline', 'strike'],
  [{ 'list': 'ordered'}, { 'list': 'bullet' }],
  [{ 'header': [1, 2, 3, false] }],
  [{ 'color': [] }, { 'background': [] }],
  ['clean']
];

const formTextSections = ref([
  { title: '1. Your Details Form', id: 'yourDetails' },
  { title: '2. Company Induction Form', id: 'employeeInduction' },
  { title: '3. Roles & Responsibilities Statement', id: 'rolesAndResponsibilities' },
  { title: '4. Literacy Test Introduction', id: 'literacyTest' },
  { title: '5. Primary Sponsor Contract', id: 'primarySponsorContract' },
  { title: '6. Annual Health Assessment Declaration', id: 'annualHealthAssessment' },
  { title: '7. Eyesight Declaration', id: 'eyesightDeclaration' },
  { title: '8. Right To Work Instructions', id: 'rightToWork' },
  { title: '9. PPE Request Text', id: 'ppeRequest' },
  { title: '10. Employee Handbook Sign-Off', id: 'employeeHandbookSignOff' },
  { title: 'Sub-Contractor Onboarding Pack', id: 'subContractorPack' }
]);

const startEditing = (id) => {
  editData.value[id] = textDataMap.value[id] || '';
  editMode.value[id] = true;
};

const cancelEditing = (id) => {
  editMode.value[id] = false;
  editData.value[id] = '';
};

const fetchClientText = async (clientId) => {
    loading.value = true;
    error.value = null;
    currentClientId.value = clientId;
    try {
        const clientDocRef = doc(db, 'clients', clientId);
        const docSnap = await getDoc(clientDocRef);
        if (docSnap.exists()) {
            textDataMap.value = docSnap.data().onboardingFormTexts || {};
        } else {
            error.value = "Client configuration record not found.";
        }
    } catch (err) {
        console.error("Registry Fetch Error:", err);
        error.value = "Failed to sync protocol registry.";
    } finally {
        loading.value = false;
    }
};

const saveText = async (formId) => {
    if (!currentClientId.value) return;
    isSaving.value = true;
    const contentToSave = editData.value[formId] || '';
    const clientDocRef = doc(db, 'clients', currentClientId.value);
    const fieldPath = `onboardingFormTexts.${formId}`;

    try {
        await updateDoc(clientDocRef, { [fieldPath]: contentToSave });
        textDataMap.value[formId] = contentToSave;
        cancelEditing(formId);
    } catch (err) {
        console.error("Save Error:", err);
        alert(`Registry Update Denied: Authorisation Failure.`);
    } finally {
        isSaving.value = false;
    }
};

onMounted(() => {
  onAuthStateChanged(auth, async (user) => {
    if (user) {
      try {
        const idTokenResult = await user.getIdTokenResult();
        const clientId = idTokenResult.claims.clientId;
        if (clientId) {
          await fetchClientText(clientId);
        } else {
          error.value = "No client association identified.";
          loading.value = false;
        }
      } catch (err) {
        error.value = "Identity verification failure.";
        loading.value = false;
      }
    } else {
      error.value = "Authentication required for registry access.";
      loading.value = false;
    }
  });
});
</script>

<style scoped>
.micro-label { @apply block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]; }

.text-display-box { 
  min-height: 10rem; 
  max-height: 40rem; 
  overflow-y: auto; 
}

/* Premium Prose Rendering */
.custom-prose-styles :deep(p) { margin-bottom: 1.25em; line-height: 1.6; color: #475569; }
.custom-prose-styles :deep(h1), .custom-prose-styles :deep(h2), .custom-prose-styles :deep(h3) {
  @apply font-black text-[#0A1630] uppercase tracking-tight mt-6 mb-4;
}
.custom-prose-styles :deep(ul) { @apply list-disc list-inside space-y-2 mb-6 ml-4; }
.custom-prose-styles :deep(strong) { @apply font-black text-[#0A1630]; }

/* Quill Toolbar Premium Overrides */
:deep(.ql-toolbar.ql-snow) {
  border: none;
  border-bottom: 1px solid #f1f5f9;
  background-color: #f8fafc;
  padding: 1rem;
}
:deep(.ql-container.ql-snow) {
  border: none;
  font-family: inherit;
  font-size: 0.875rem;
}
:deep(.ql-editor) {
  padding: 2rem;
  line-height: 1.6;
}

.animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
</style>